import React, { useContext } from 'react';
import TextVariantContext from '@aurora/shared-client/components/context/TextVariantContext';
import type {
  MessageSearchViewFragment,
  QuickMessageSearchViewFragment
} from '../../../types/graphql-types';
import type { ItemType } from '../../../types/enums';
import { MessageViewVariant } from '../../../types/enums';
import mergeVariantProps from './MessageSearchViewDefaultProps';
import type { ItemViewFC } from '../../entities/types';
import type { MessageViewCommonProps } from '../MessageView/types';
import type { AttachmentSearchConnection } from '@aurora/shared-generated/types/graphql-schema-types';
import MessageSearchViewInline from './MessageSearchViewInline/MessageSearchViewInline';

/**
 * The base message search view component that renders the message view specified by
 * the <code>variant</code> parameter. This component should be used anywhere a message search result is rendered.
 *
 * @author Amit Agrawal
 */
const MessageSearchView: ItemViewFC<
  MessageSearchViewFragment | QuickMessageSearchViewFragment,
  ItemType.MESSAGE_SEARCH,
  MessageViewVariant
> = ({ entity, variant = { type: MessageViewVariant.INLINE }, className }) => {
  const { message } = entity;
  const textVariantContext = useContext(TextVariantContext);
  const defaultProps = mergeVariantProps<MessageViewCommonProps>(variant);
  const isQuickMessageSearch = entity.__typename === 'QuickMessageSearchResult';

  return (
    <TextVariantContext.Provider value={{ ...textVariantContext, message, node: message.board }}>
      <MessageSearchViewInline
        entity={entity}
        searchData={{
          searchSnippet: entity.snippet,
          attachments: !isQuickMessageSearch
            ? (entity?.attachments as AttachmentSearchConnection)
            : null
        }}
        useMatchingReplies={!isQuickMessageSearch}
        className={className}
        messageInlineViewProps={{ ...defaultProps, ...variant.props }}
      />
    </TextVariantContext.Provider>
  );
};

export default MessageSearchView;
